import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ForceCaseUtils } from '../../../../shared/utils/force-case-utils';
import { addPartToDataMM07, updateMM07ActivePartAndRecalc, validateMM07LoadWeight } from '../../state/cr7.actions';
import { DataMM07Part } from '../../../models/criterions/DataMM07Part';
import { DataMM07 } from '../../../models/criterions/DataMM07';
import {
    selectActiveConfigDefMM07Parts,
    selectPullAndPushDefId,
    selectPullAndPushExecutionConditionDropdownOptions,
    selectTransferExecutionConditionDropdownOptions,
    selectPostures,
    selectCR7ActivePartRzValue,
    selectCR7ActivePartColor,
    selectCR7ActivePart,
    selectCR7IsWaitingForRecalc
} from '../../state/cr7.selectors';
import { map, withLatestFrom, Observable, tap } from 'rxjs';
import { selectUserLanguage } from '../../../../core/state/core.selectors';
import { MM07ForceCaseType } from './models/MM07ForceCaseType';
import { TranslationUtils } from '../../../../shared/utils/translation-utils';
import { DataMM07PostureItem } from './models/DataMM07PostureItem';
import { Cr7ForceCaseImagesComponent } from './cr7-force-case-images/cr7-force-case-images.component';
import { displayToastError } from '../../../../core/state/core.actions';
import { FormControlUtils } from '../../../../shared/utils/form-control-utils';
import { selectAnalysisTechnology, selectProcessFrequency } from '../../../state/analysis.selectors';
import { DefMM07Part } from '../../../../masterdata/models/config-version/DefMM07Part';
import { SeraDropdownMapperService } from '../../../../shared/services/sera-dropdown-mapper/sera-dropdown-mapper.service';

@Component({
    selector: 'cr7-force-case-popup',
    templateUrl: './cr7-force-case-popup.component.html',
    styleUrls: ['./cr7-force-case-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Cr7ForceCasePopupComponent implements AfterViewInit {
    @ViewChild(Cr7ForceCaseImagesComponent) forceCaseImagesComponent!: Cr7ForceCaseImagesComponent;

    forceCasePopUpForm: UntypedFormGroup = this.formBuilder.group({
        defPartId: [null, Validators.required],
        defPostureId: [null, Validators.required],
        defExecutionConditionId: [null, Validators.required],
        description: [null, Validators.required],
        loadWeight: [null, Validators.required],
        frequencyPerShift: [null, Validators.required],
        realFrequencyPerAnalysis: [null],
        divisor: [null],
        distanceShort: [null],
        distanceLong: [null],
        realDistanceShort: [null],
        realDistanceLong: [null],
        positioningPrecision: [null],
        movementVelocity: [null],
        oneHanded: [false],
        cartWithCasterWheels: [false],
        postures: [null],
        commentPart: [null],
        isPercentageToggle: [false],
        editMode: [true],
        rzValue: [null],
        color: [null]
    });

    @Output()
    closeForceCasePopupEvent: EventEmitter<boolean> = new EventEmitter();

    @Output()
    saveForceCasePopupEvent: EventEmitter<boolean> = new EventEmitter();

    @Input()
    dataMM07!: DataMM07;

    @Input()
    isVisible: boolean = false;

    @Input()
    activePart?: DataMM07Part;

    @Input()
    readonly!: boolean;

    $tabHolderItems: Observable<MenuItem[]> = this.store.select(selectActiveConfigDefMM07Parts).pipe(
        withLatestFrom(this.store.select(selectUserLanguage)),
        map(([parts, userLanguage]) => {
            this.tabHolderItems = parts!;
            return parts!.map(
                part =>
                    ({
                        label: TranslationUtils.translateTextValue(part.description, userLanguage),
                        id: part.id.toString(),
                        command: () => this.handleTabChange(part.id)
                    } as MenuItem)
            );
        })
    );

    currentActiveMenuItem?: MenuItem;

    $pullAndPushDefId = this.store.select(selectPullAndPushDefId).pipe(
        map(pullAndPushDefId => {
            this.pullAndPushDefIndex = this.tabHolderItems.findIndex(item => item.id === pullAndPushDefId)!;
        })
    );

    $pullAndPushExecutionConditionsOptions = this.store.select(selectPullAndPushExecutionConditionDropdownOptions);
    $transferExecutionConditionsOptions = this.store.select(selectTransferExecutionConditionDropdownOptions);

    $analysisTechnology = this.store.select(selectAnalysisTechnology).pipe(
        tap(analysisTechnology => {
            this.analysisTechnologyCode = analysisTechnology;
            this.updateDivisorValidators();
        })
    );

    $analysisProcessFrequency = this.store.select(selectProcessFrequency).pipe(
        tap(analysisProcessFrequency => {
            this.analysisProcessFrequency = analysisProcessFrequency;
            this.updateProcessFrequencyValidators();
        })
    );

    $postureItems = this.store.select(selectPostures).pipe(
        withLatestFrom(this.store.select(selectUserLanguage)),
        map(([postures, userLanguage]) => {
            this.pullAndPushPostures = ForceCaseUtils.mapPostures(
                postures,
                this.activePart?.postures,
                this.activePart?.defPostureId ?? postures?.[0]?.id!,
                this.activePart?.defPartId!,
                true,
                userLanguage!
            );
            this.transferPostures = ForceCaseUtils.mapPostures(
                postures,
                this.activePart?.postures,
                this.activePart?.defPostureId ?? postures?.[0]?.id!,
                this.activePart?.defPartId!,
                false,
                userLanguage!
            );
        })
    );

    $activePartRzValue = this.store.select(selectCR7ActivePartRzValue);

    $activePartColor = this.store.select(selectCR7ActivePartColor);

    $activePart = this.store.select(selectCR7ActivePart).pipe(
        tap(activePart => {
            this.activePart = {
                ...activePart!,
                isPercentageToggle: activePart?.isPercentageToggle ?? (activePart?.postures != null && activePart?.postures.length > 0)
            };
            this.buildForm();
        })
    );

    $selectIsWaitingForRecalc = this.store.select(selectCR7IsWaitingForRecalc);

    forceCaseLoaded: boolean = false;
    ranValidation: boolean = false;
    isNewForceCase: boolean = true;

    tabHolderItems: DefMM07Part[] = [];
    currentSelectedTab: number = 0;
    currentSelectedTabIndex: number = 0;

    positioningPrecisionOptions = ForceCaseUtils.positioningPrecisionOptions;
    movementVelocityOptions = ForceCaseUtils.movementVelocityOptions;

    transferPostures: DataMM07PostureItem[] = [];
    pullAndPushPostures: DataMM07PostureItem[] = [];

    isPullAndPush = false;
    pullAndPushDefIndex = 3;
    pullPushTabHolderItems: MenuItem[] = [
        { id: 'distanceShort', label: 'PAGE.MM_07.FREQUENCY_LESS_5M.LBL' },
        { id: 'distanceLong', label: 'PAGE.MM_07.DISTANCE_MORE_5M.LBL' }
    ];
    pullPushActiveMenuItem: MenuItem = this.pullPushTabHolderItems[0];

    analysisTechnologyCode?: string;
    analysisProcessFrequency?: number;

    constructor(
        private readonly store: Store,
        private readonly formBuilder: FormBuilder,
        private readonly translateService: TranslateService,
        private readonly seraDropdownMapperService: SeraDropdownMapperService
    ) {}

    ngAfterViewInit() {
        this.isNewForceCase = !this.activePart || (!this.activePart.sourceId && !this.activePart.id);
        if (this.isNewForceCase) {
            this.buildEmptyForceCase();
        }
        this.buildForm();
        this.updateTab(this.activePart!.defPartId);
        this.isPullAndPush = this.currentSelectedTabIndex === this.pullAndPushDefIndex;
        this.updatePullAndPushValidators();
        this.updateToggleValidators(this.forceCasePopUpForm.get('isPercentageToggle')?.value);
        this.selectImage(this.activePart!.defPostureId);
        this.updateActivePartAndRecalc();
        this.forceCaseLoaded = true;
    }

    buildEmptyForceCase() {
        this.activePart = {
            defPartId: this.tabHolderItems[0].id,
            isPercentageToggle: false,
            sourceId: Date.now() + Math.floor(Math.random() * 1000000),
            sortOrder: this.dataMM07?.parts?.length ?? 0
        } as DataMM07Part;
    }

    buildForm() {
        this.forceCasePopUpForm.patchValue({
            defPartId: this.activePart?.defPartId,
            defPostureId: this.activePart?.defPostureId,
            defExecutionConditionId: this.activePart?.defExecutionConditionId?.toString(),
            description: this.activePart?.description,
            loadWeight: this.activePart?.loadWeight,
            frequencyPerShift: this.activePart?.frequencyPerShift,
            realFrequencyPerAnalysis: this.activePart?.realFrequencyPerAnalysis,
            divisor: this.activePart?.divisor,
            distanceShort: this.activePart?.distanceShort,
            distanceLong: this.activePart?.distanceLong,
            positioningPrecision: this.activePart?.positioningPrecision,
            movementVelocity: this.activePart?.movementVelocity,
            oneHanded: this.activePart?.oneHanded,
            cartWithCasterWheels: this.activePart?.cartWithCasterWheels,
            postures: this.activePart?.postures,
            commentPart: this.activePart?.commentPart,
            isPercentageToggle: this.activePart?.isPercentageToggle,
            editMode: true,
            realDistanceLong: this.activePart?.realDistanceLong,
            realDistanceShort: this.activePart?.realDistanceShort,
            rzValue: this.activePart?.rzValue,
            color: this.activePart?.color
        });
    }

    updatePullAndPushValidators() {
        if (this.isPullAndPush) {
            this.onPullPushMenuItemChange(this.forceCasePopUpForm.value.distanceLong ? this.pullPushTabHolderItems[1] : this.pullPushTabHolderItems[0]);
            this.forceCasePopUpForm.get('positioningPrecision')?.setValidators(Validators.required);
            this.forceCasePopUpForm.get('movementVelocity')?.setValidators(Validators.required);
            this.forceCasePopUpForm.get('frequencyPerShift')?.clearValidators();
            this.forceCasePopUpForm.get('realFrequencyPerAnalysis')?.clearValidators();
            this.pullPushActiveMenuItem.id === 'distanceShort' ? this.handleDistanceShort() : this.handleDistanceLong();
            this.updateFormValidityForDistanceShortAndLong();
        } else {
            this.forceCasePopUpForm.get('frequencyPerShift')?.setValidators(Validators.required);
            this.forceCasePopUpForm.get('positioningPrecision')?.clearValidators();
            this.forceCasePopUpForm.get('movementVelocity')?.clearValidators();
            this.forceCasePopUpForm.get('distanceShort')?.clearValidators();
            this.forceCasePopUpForm.get('distanceLong')?.clearValidators();
            this.forceCasePopUpForm.get('realDistanceShort')?.clearValidators();
            this.forceCasePopUpForm.get('realDistanceLong')?.clearValidators();
            this.updateProcessFrequencyValidators();
        }
        FormControlUtils.updateFormValueAndValidity(this.forceCasePopUpForm);
    }

    updateProcessFrequencyValidators() {
        const realFrequencyControl = this.forceCasePopUpForm.get('realFrequencyPerAnalysis');
        const frequencyShiftControl = this.forceCasePopUpForm.get('frequencyPerShift');

        if (this.isProcessFrequencyFilled() || this.isTMOTKBTechnology()) {
            realFrequencyControl?.setValidators(this.zeroOrNullValidator());
        }

        if (this.isProcessFrequencyFilled()) {
            frequencyShiftControl?.clearValidators();
        }

        realFrequencyControl?.updateValueAndValidity();
        frequencyShiftControl?.updateValueAndValidity();
    }

    updateDivisorValidators() {
        const divisorControl = this.forceCasePopUpForm.get('divisor');

        if (this.isTMOTechnology()) {
            divisorControl?.setValidators(Validators.required);
        }

        divisorControl?.updateValueAndValidity();
    }

    updateToggleValidators(isPercentageToggle: boolean) {
        if (isPercentageToggle) {
            this.forceCasePopUpForm.get('defPostureId')?.clearValidators();
        } else {
            this.forceCasePopUpForm.get('defPostureId')?.setValidators(Validators.required);
        }
        this.forceCasePopUpForm.get('defPostureId')?.updateValueAndValidity();
    }

    getToolTipBasedOnType() {
        if (this.isPullAndPush) {
            return '';
        }

        const tooltips: { [key: string]: string } = {
            [MM07ForceCaseType.LIFT]: 'FORCE_CASE.MM_07.LIFT.TOOLTIP.LBL',
            [MM07ForceCaseType.CARRY]: 'FORCE_CASE.MM_07.CARRY.TOOLTIP.LBL',
            [MM07ForceCaseType.HOLD]: 'FORCE_CASE.MM_07.HOLD.TOOLTIP.LBL'
        };

        return this.translateService.instant(tooltips[this.currentSelectedTabIndex]);
    }

    isBodyPostureInvalid() {
        const bodyPosture = this.forceCasePopUpForm.get('defPostureId')?.value;
        return !bodyPosture && this.ranValidation && !this.forceCasePopUpForm.get('isPercentageToggle')?.value;
    }

    isBodyPostureSumInvalid() {
        const percentageMax = this.forceCasePopUpForm
            .get('postures')
            ?.value?.reduce((acc: number, posture: DataMM07PostureItem) => acc + (posture.percentage ?? 0), 0);
        const isValid = percentageMax !== 100 && this.forceCasePopUpForm.get('isPercentageToggle')?.value;
        return isValid && this.ranValidation;
    }

    isBodyPostureInvalidOrSumInvalid(): boolean {
        const isBodyPostureInvalid = this.isBodyPostureInvalid();
        const isBodyPostureSumInvalid = this.isBodyPostureSumInvalid();
        if (isBodyPostureSumInvalid) {
            this.store.dispatch(displayToastError({ message: 'PAGE.MM_07.BODY_POSTURE_SUM_ERROR.LBL' }));
        }

        return isBodyPostureInvalid || isBodyPostureSumInvalid;
    }

    updateTab(defPartId: number) {
        this.currentSelectedTab = defPartId;
        this.currentSelectedTabIndex = this.tabHolderItems.findIndex(item => item.id === defPartId);
    }

    handleTabChange(selectedTab: number) {
        const wasPullAndPush = this.isPullAndPush;

        if (this.currentSelectedTabIndex === 0 && this.forceCaseImagesComponent.isToShowInputForBodyPosture) {
            this.forceCaseImagesComponent.handleBodyPostureToggleChange(false);
        }

        const selectedIndex = this.tabHolderItems.findIndex(item => item.id === selectedTab);
        this.isPullAndPush = selectedIndex === this.pullAndPushDefIndex;

        if (wasPullAndPush !== this.isPullAndPush) {
            this.clearFieldsAndDeselectImages();
            this.updatePullAndPushValidators();
            if (this.isPullAndPush) {
                this.handleDistanceShort();
            }
        }

        this.ranValidation = false;
        this.updateTab(selectedTab);
        this.forceCasePopUpForm.patchValue({ defPartId: selectedTab });
        this.updateImageValidity(false);
        this.updateToggleValidators(this.forceCasePopUpForm.get('isPercentageToggle')?.value);
    }

    private updatePostures(postureItems: DataMM07PostureItem[]) {
        if (this.isPullAndPush) {
            this.pullAndPushPostures = postureItems;
        } else {
            this.transferPostures = postureItems;
        }
    }

    handleImageClick(event: { defPostureId: number; postureItems: DataMM07PostureItem[] }) {
        this.updatePostures(event.postureItems);
        this.forceCasePopUpForm.patchValue({ defPostureId: event.defPostureId });
        this.updateImageValidity(false);
        this.updateActivePartAndRecalc();
    }

    handlePercentageToggle(event: { defPostureId: number; value: boolean; postureItems: DataMM07PostureItem[] }) {
        if (event.value) {
            this.selectImage(event.defPostureId);
            this.updatePostures(event.postureItems);
            this.forceCasePopUpForm.patchValue({
                defPostureId: null,
                postures: this.areAllPosturesNull(event.postureItems) ? null : event.postureItems,
                isPercentageToggle: event.value
            });
        } else {
            this.forceCasePopUpForm.patchValue({
                defPostureId: null,
                postures: null,
                isPercentageToggle: event.value
            });
            this.updatePostures(event.postureItems);
        }
        this.updateImageValidity(false);
        this.updateToggleValidators(event.value);
        this.updateActivePartAndRecalc();
    }

    areAllPosturesNull(postureItems: DataMM07PostureItem[]): boolean {
        return postureItems.every(posture => posture.percentage === null);
    }

    handleInputBlur(postureItems: DataMM07PostureItem[]) {
        this.updatePostures(postureItems);
        this.forceCasePopUpForm.patchValue({ postures: this.areAllPosturesNull(postureItems) ? null : postureItems });
        const isInvalid = !this.forceCasePopUpForm.get('isPercentageToggle')?.value ? this.isBodyPostureInvalidOrSumInvalid() : false;
        this.updateImageValidity(isInvalid);
        this.updateActivePartAndRecalc();
    }

    clearFieldsAndDeselectImages() {
        this.deselectImages();
        this.forceCasePopUpForm.patchValue({
            defPostureId: null,
            defExecutionConditionId: null,
            frequencyPerShift: null,
            realFrequencyPerAnalysis: null,
            oneHanded: false,
            cartWithCasterWheels: false,
            positioningPrecision: null,
            movementVelocity: null,
            distanceShort: null,
            distanceLong: null,
            realDistanceShort: null,
            realDistanceLong: null,
            postures: null
        });
    }

    deselectImages() {
        this.isPullAndPush
            ? this.pullAndPushPostures.forEach(posture => (posture.image.isSelected = false))
            : this.transferPostures.forEach(posture => (posture.image.isSelected = false));
    }

    updatePostureItems(updateFn: (posture: DataMM07PostureItem) => DataMM07PostureItem) {
        const updatePostures = (postures: DataMM07PostureItem[]) => postures.map(updateFn);

        if (this.isPullAndPush) {
            this.pullAndPushPostures = updatePostures(this.pullAndPushPostures);
        } else {
            this.transferPostures = updatePostures(this.transferPostures);
        }
    }

    updateImageValidity(isInvalid: boolean) {
        this.updatePostureItems(posture => ({
            ...posture,
            image: { ...posture.image, isInvalid: isInvalid }
        }));
    }

    selectImage(defPostureId: number) {
        this.updatePostureItems(posture => ({
            ...posture,
            percentage: this.forceCasePopUpForm.get('isPercentageToggle')?.value ? posture.percentage : posture.defPostureId === defPostureId ? 100 : null,
            image: { ...posture.image, isSelected: posture.defPostureId === defPostureId || posture.percentage! > 0 }
        }));
    }

    getActiveTabItem(tabHolderItems: MenuItem[]) {
        const activeTabItem = tabHolderItems.find(item => item.id === this.currentSelectedTab.toString());
        this.currentActiveMenuItem = activeTabItem || tabHolderItems[0];
        return this.currentActiveMenuItem;
    }

    getPositioningPrecisionOptions() {
        return this.seraDropdownMapperService.translateDropdownOptions(this.positioningPrecisionOptions);
    }

    getMovementVelocityOptions() {
        return this.seraDropdownMapperService.translateDropdownOptions(this.movementVelocityOptions);
    }

    handleCommentInputChanges(value: string) {
        this.forceCasePopUpForm.patchValue({ commentPart: value });
    }

    validateForceCase() {
        this.ranValidation = true;
        const isInvalid = !this.forceCasePopUpForm.get('isPercentageToggle')?.value ? this.isBodyPostureInvalidOrSumInvalid() : false;
        this.updateImageValidity(isInvalid);
        return this.forceCasePopUpForm.valid && !this.isBodyPostureInvalidOrSumInvalid();
    }

    onAddButton() {
        this.saveForceCasePopupEvent.emit();
        FormControlUtils.markAllControlsAsDirty(this.forceCasePopUpForm);
        if (this.validateForceCase()) {
            this.store.dispatch(
                addPartToDataMM07({
                    dataMM07: { ...this.dataMM07, activePart: { ...this.activePart, ...this.getMappedForm(), sortOrder: this.activePart!.sortOrder ?? 0 } }
                })
            );
        }
    }

    getLabelBasedOnTabIndex(): string {
        const labels = [
            'PAGE.FORCE_CASE.FREQUENCY_LIFT.LBL',
            'PAGE.FORCE_CASE.FREQUENCY_HOLD.LBL',
            'PAGE.FORCE_CASE.FREQUENCY_CARRY.LBL',
            'PAGE.MM_07_LOAD_CASE_FREQUENCY_LIFT'
        ];
        const label = this.translateService.instant(labels[this.currentSelectedTabIndex] || labels[3]);
        return label + (!this.isProcessFrequencyFilled() ? ' *' : '');
    }

    isProcessFrequencyFilled(): boolean {
        return this.analysisProcessFrequency != null;
    }

    isTMOTechnology(): boolean {
        return this.analysisTechnologyCode === 'TMO';
    }

    isTBKTechnology(): boolean {
        return this.analysisTechnologyCode === 'TKB';
    }

    isTMOTKBTechnology(): boolean {
        return this.isTMOTechnology() || this.isTBKTechnology();
    }

    getRealFrequencyPerAnalysisLabel(): string {
        const labels = [
            this.isTMOTKBTechnology() ? 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_LIFT' : 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_PROCESS_LIFT',
            this.isTMOTKBTechnology() ? 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_HOLD' : 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_PROCESS_HOLD',
            this.isTMOTKBTechnology() ? 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_CARRY' : 'PAGE.MM_07_REAL_LOAD_CASE_FREQUENCY_PROCESS_CARRY'
        ];
        return labels[this.currentSelectedTabIndex] ? this.translateService.instant(labels[this.currentSelectedTabIndex]) : '';
    }

    getInputStyleClassValidation(formControlName: string): string {
        return this.ranValidation ? FormControlUtils.updateStyleClassValidity(this.forceCasePopUpForm, formControlName) : '';
    }

    onPullPushMenuItemChange(menu: MenuItem) {
        if (this.pullPushActiveMenuItem?.id === menu.id) {
            return;
        }
        this.pullPushActiveMenuItem = menu;
        menu.id === 'distanceShort' ? this.handleDistanceShort() : this.handleDistanceLong();
        this.updateFormValidityForDistanceShortAndLong();
        this.updateActivePartAndRecalc();
    }

    onTabHolderChange(menu: MenuItem) {
        if (this.currentActiveMenuItem?.id !== menu.id) {
            this.handleTabChange(+menu.id!);
            this.updateActivePartAndRecalc();
        }
    }

    getMappedForm() {
        return {
            ...this.forceCasePopUpForm.value,
            defExecutionConditionId:
                this.forceCasePopUpForm.value.defExecutionConditionId != null
                    ? +this.forceCasePopUpForm.value.defExecutionConditionId
                    : this.forceCasePopUpForm.value.defExecutionConditionId
        };
    }

    updateActivePartAndRecalc() {
        if (!this.forceCaseLoaded) return;
        this.store.dispatch(updateMM07ActivePartAndRecalc({ activePart: { ...this.activePart, ...this.getMappedForm() } }));
    }

    updateActivePartAndRecalcOnLoadWeightChange() {
        if (!this.forceCaseLoaded) return;
        this.callValidationsOfLoadWeight();
        this.store.dispatch(updateMM07ActivePartAndRecalc({ activePart: { ...this.activePart, ...this.getMappedForm() } }));
    }

    callValidationsOfLoadWeight() {
        const loadWeight = this.forceCasePopUpForm.get('loadWeight')?.value;
        this.store.dispatch(validateMM07LoadWeight({ activePart: { ...this.activePart!, loadWeight: loadWeight } }));
    }

    handleDistanceShort() {
        this.forceCasePopUpForm.patchValue({ distanceLong: null });
        this.forceCasePopUpForm.get('distanceShort')?.setValidators(Validators.required);
        this.forceCasePopUpForm.get('distanceLong')?.clearValidators();

        if (this.isProcessFrequencyFilled() || this.isTMOTKBTechnology()) {
            this.forceCasePopUpForm.patchValue({ realDistanceLong: null });
            this.forceCasePopUpForm.get('realDistanceLong')?.clearValidators();
            this.forceCasePopUpForm.get('realDistanceShort')?.setValidators(this.zeroOrNullValidator());

            if (this.isProcessFrequencyFilled()) {
                this.forceCasePopUpForm.get('distanceShort')?.clearValidators();
            }
        }
    }

    handleDistanceLong() {
        this.forceCasePopUpForm.patchValue({ distanceShort: null });
        this.forceCasePopUpForm.get('distanceLong')?.setValidators(Validators.required);
        this.forceCasePopUpForm.get('distanceShort')?.clearValidators();

        if (this.isProcessFrequencyFilled() || this.isTMOTKBTechnology()) {
            this.forceCasePopUpForm.patchValue({ realDistanceShort: null });
            this.forceCasePopUpForm.get('realDistanceShort')?.clearValidators();
            this.forceCasePopUpForm.get('realDistanceLong')?.setValidators(this.zeroOrNullValidator());

            if (this.isProcessFrequencyFilled()) {
                this.forceCasePopUpForm.get('distanceLong')?.clearValidators();
            }
        }
    }

    updateFormValidityForDistanceShortAndLong() {
        this.forceCasePopUpForm.get('distanceShort')?.updateValueAndValidity();
        this.forceCasePopUpForm.get('distanceLong')?.updateValueAndValidity();
        this.forceCasePopUpForm.get('realDistanceShort')?.updateValueAndValidity();
        this.forceCasePopUpForm.get('realDistanceLong')?.updateValueAndValidity();
    }

    zeroOrNullValidator(): ValidatorFn {
        return (control: AbstractControl<number | null>): ValidationErrors | null => {
            if (control.value === 0 || control.value === null || control.value === undefined) {
                return { zero: true, required: true };
            }
            return null;
        };
    }
}
